// Client-side helper for the NASR waypoint suggester endpoint. Used by the
// plate-correction editor surfaces (ProcedureTab, admin queue, review queue,
// my-corrections) to merge NASR-suggested waypoints into the editor's
// EditorFix[] list.
//
// Endpoint: /api/plates/suggested-waypoints — see api/plates/suggested-waypoints.ts
// Spec:     data/nasr-waypoint-suggester-spec.md

import type { EditorFix } from '../plates/PlateCorrectionEditor';

export interface SuggestedWaypoint {
  ident: string;
  source: 'plate-text' | 'geographic-nearby';
  origin: 'nasr-navaid' | 'nasr-fix';
  navaidType?: string;
  fixCategory?: string;
  name: string;
  lat: number;
  lon: number;
  elevFt: number | null;
  freq?: string;
  distanceFromArpNm: number;
  bearingFromArpDeg?: number;
  inferredRole?: 'iaf' | 'faf' | 'map' | 'navaid' | 'unknown';
  pageReferences: number[];
}

export interface SuggestedWaypointsResponse {
  pdfName: string;
  cycle: string;
  airport: string | null;
  candidates: SuggestedWaypoint[];
  notFound: string[];
  mode: 'text-primary' | 'text+geographic' | 'geographic-only';
  geographicFallbackUsed: boolean;
  nasrCycle: string;
  textExtractMs: number;
  nasrLookupMs: number;
}

/**
 * Fetch suggested waypoints from the server. Returns null on transport
 * failure so the editor can degrade gracefully (CIFP-only or manual entry).
 */
export async function fetchSuggestedWaypoints(
  pdfName: string,
  cycle: string,
  airport: string | null,
  signal?: AbortSignal,
): Promise<SuggestedWaypointsResponse | null> {
  try {
    const url = new URL('/api/plates/suggested-waypoints', window.location.origin);
    url.searchParams.set('pdfName', pdfName);
    url.searchParams.set('cycle', cycle);
    if (airport) url.searchParams.set('airport', airport);
    const resp = await fetch(url.toString(), { signal });
    if (!resp.ok) return null;
    return await resp.json() as SuggestedWaypointsResponse;
  } catch {
    return null;
  }
}

/**
 * Convert a SuggestedWaypoint to the editor's EditorFix shape, preserving
 * source/origin/name/navaidType/distance for badge rendering.
 */
export function suggestedToEditorFix(s: SuggestedWaypoint): EditorFix {
  return {
    ident: s.ident,
    lat: s.lat,
    lon: s.lon,
    source: s.source,
    navaidType: s.navaidType,
    name: s.name,
    distanceFromArpNm: Number.isFinite(s.distanceFromArpNm) ? s.distanceFromArpNm : undefined,
    bearingFromArpDeg: s.bearingFromArpDeg,
    inferredRole: s.inferredRole,
    // 'navaid' for VOR/VORTAC/NDB, 'iaf' for fixes (the editor treats these
    // as anchor-eligible regardless; the type label is informational).
    type: s.origin === 'nasr-navaid' ? 'navaid' : 'iaf',
  };
}
